import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { GoogleOAuthProvider } from '@react-oauth/google';
import useAuthStore from './store/authStore';
import useUIStore from './store/uiStore';
import { useEffect, lazy, Suspense } from 'react';
import useMobile, { usePlatform } from './hooks/useMobile';
import ErrorBoundary from './components/ErrorBoundary';

// Build version for cache busting - v5.2.0
const APP_VERSION = '5.2.0';
if (import.meta.env.DEV) {
  console.log(`LensVerse v${APP_VERSION} loaded`);
}

// ---------------------------------------------------------------------------
// Eagerly loaded — always needed on every authenticated page
// ---------------------------------------------------------------------------
import AppLayout from './components/layout/AppLayout';
import SettingsLayout from './components/layout/SettingsLayout';
import MobileLayout from './components/mobile/MobileLayout';
import IdleTimeout from './components/auth/IdleTimeout';

// Auth/landing — loaded eagerly since they're entry points
import Landing from './pages/Home';
import Login from './pages/Login';
import Signup from './pages/Signup';
import SignupSuccess from './pages/SignupSuccess';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';

// Core pages — loaded eagerly (frequently visited)
import Dashboard from './pages/documents/Dashboard';
import Documents from './pages/documents/Documents';
import DocumentDetail from './pages/documents/DocumentDetail';

// Mobile pages — loaded eagerly (needed for responsive layout)
import MobileHome from './pages/mobile/MobileHome';
import MobileSettings from './pages/mobile/MobileSettings';

// ---------------------------------------------------------------------------
// Lazy-loaded — only downloaded when the user navigates to them
// ---------------------------------------------------------------------------

// Suspense fallback for lazy routes
const LazyFallback = () => (
  <div className="flex items-center justify-center py-20">
    <div className="spinner-theme w-6 h-6" />
  </div>
);

// Document pages
const Upload = lazy(() => import('./pages/documents/Upload'));
const DocumentCrop = lazy(() => import('./pages/documents/DocumentCrop'));
const PageEditor = lazy(() => import('./pages/documents/PageEditor'));
const RecycleBin = lazy(() => import('./pages/documents/RecycleBin'));
const StructuredDataDemo = lazy(() => import('./pages/documents/StructuredDataDemo'));

// Tool pages
const ToolCatalogPage = lazy(() => import('./pages/tools/ToolCatalogPage'));
const WordToPdfPage = lazy(() => import('./pages/documents/tools/WordToPdfPage'));
const PdfToWordPage = lazy(() => import('./pages/documents/tools/PdfToWordPage'));
const UnzipPage = lazy(() => import('./pages/documents/tools/UnzipPage'));
const TranslatePdfPage = lazy(() => import('./pages/documents/tools/TranslatePdfPage'));
const MergePdfPage = lazy(() => import('./pages/documents/tools/MergePdfPage'));
const SplitPdfPage = lazy(() => import('./pages/documents/tools/SplitPdfPage'));
const RemovePagesPage = lazy(() => import('./pages/documents/tools/RemovePagesPage'));
const ExtractPagesPage = lazy(() => import('./pages/documents/tools/ExtractPagesPage'));
const OrganizePdfPage = lazy(() => import('./pages/documents/tools/OrganizePdfPage'));
const ScanToPdfPage = lazy(() => import('./pages/documents/tools/ScanToPdfPage'));
const CompressPdfPage = lazy(() => import('./pages/documents/tools/CompressPdfPage'));
const RepairPdfPage = lazy(() => import('./pages/documents/tools/RepairPdfPage'));
const OcrPdfPage = lazy(() => import('./pages/documents/tools/OcrPdfPage'));
const PptToPdfPage = lazy(() => import('./pages/documents/tools/PptToPdfPage'));
const ExcelToPdfPage = lazy(() => import('./pages/documents/tools/ExcelToPdfPage'));
const HtmlToPdfPage = lazy(() => import('./pages/documents/tools/HtmlToPdfPage'));
const JpgToPdfPage = lazy(() => import('./pages/documents/tools/JpgToPdfPage'));
const PdfToJpgPage = lazy(() => import('./pages/documents/tools/PdfToJpgPage'));
const PdfToPptPage = lazy(() => import('./pages/documents/tools/PdfToPptPage'));
const PdfToExcelPage = lazy(() => import('./pages/documents/tools/PdfToExcelPage'));
const PdfToPdfAPage = lazy(() => import('./pages/documents/tools/PdfToPdfAPage'));
const RotatePdfPage = lazy(() => import('./pages/documents/tools/RotatePdfPage'));
const AddPageNumbersPage = lazy(() => import('./pages/documents/tools/AddPageNumbersPage'));
const AddWatermarkPage = lazy(() => import('./pages/documents/tools/AddWatermarkPage'));
const EditPdfPage = lazy(() => import('./pages/documents/tools/EditPdfPage'));
const ProtectPdfPage = lazy(() => import('./pages/documents/tools/ProtectPdfPage'));
const UnlockPdfPage = lazy(() => import('./pages/documents/tools/UnlockPdfPage'));
const RedactPdfPage = lazy(() => import('./pages/documents/tools/RedactPdfPage'));
const FlattenPdfPage = lazy(() => import('./pages/documents/tools/FlattenPdfPage'));
const ExtractTablesPage = lazy(() => import('./pages/documents/tools/ExtractTablesPage'));
const ExtractImagesPage = lazy(() => import('./pages/documents/tools/ExtractImagesPage'));
const PdfToTextPage = lazy(() => import('./pages/documents/tools/PdfToTextPage'));
const ComparePdfPage = lazy(() => import('./pages/documents/tools/ComparePdfPage'));

// Admin pages
const EmailTemplates = lazy(() => import('./pages/admin/EmailTemplates'));
const ToolSettingsPage = lazy(() => import('./pages/admin/ToolSettingsPage'));
const RolesManagement = lazy(() => import('./pages/admin/RolesManagement'));
const RoleWizard = lazy(() => import('./pages/admin/RoleWizard'));
const DocumentTypes = lazy(() => import('./pages/admin/DocumentTypes'));
const APITokens = lazy(() => import('./pages/admin/APITokens'));
const Users = lazy(() => import('./pages/admin/Users'));
const Settings = lazy(() => import('./pages/admin/Settings'));
const OrganisationSettings = lazy(() => import('./pages/admin/OrganisationSettings'));
const AdminAPIDocs = lazy(() => import('./pages/admin/AdminAPIDocs'));
const TenantManagement = lazy(() => import('./pages/admin/TenantManagement'));
const OrgUsersList = lazy(() => import('./pages/admin/organization/OrgUsersList'));
const OrgUserForm = lazy(() => import('./pages/admin/organization/OrgUserForm'));
const CustomFields = lazy(() => import('./pages/admin/organization/CustomFields'));

// Analytics pages
const MyActivity = lazy(() => import('./pages/analytics/MyActivity'));
const OrgOverview = lazy(() => import('./pages/analytics/OrgOverview'));
const PlatformOverview = lazy(() => import('./pages/analytics/PlatformOverview'));

// Other pages
const SecurityPage = lazy(() => import('./pages/Security'));
const FeedbackSupport = lazy(() => import('./pages/feedback/FeedbackSupport'));
const FineTune = lazy(() => import('./pages/finetune/FineTune'));
const IDPList = lazy(() => import('./pages/idp/IDPList'));
const IDPDetail = lazy(() => import('./pages/idp/IDPDetail'));
const EnterpriseUpgrade = lazy(() => import('./pages/user/EnterpriseUpgrade'));
const PublicApiDocs = lazy(() => import('./pages/PublicApiDocs'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const UserAPIKeys = lazy(() => import('./pages/user/UserAPIKeys'));

// Connector Hub pages (superuser only)
const ConnectionsPage = lazy(() => import('./pages/connectors/ConnectionsPage'));
const ConnectionDetailPage = lazy(() => import('./pages/connectors/ConnectionDetailPage'));
const CreateConnectionWizard = lazy(() => import('./pages/connectors/CreateConnectionWizard'));
const ConnectorsPage = lazy(() => import('./pages/connectors/ConnectorsPage'));
const AutomationsPage = lazy(() => import('./pages/connectors/AutomationsPage'));
const AutomationCanvasPage = lazy(() => import('./pages/connectors/AutomationCanvasPage'));
const TemplatesPage = lazy(() => import('./pages/connectors/TemplatesPage'));
const RunsPage = lazy(() => import('./pages/connectors/RunsPage'));
const RunDetailPage = lazy(() => import('./pages/connectors/RunDetailPage'));
const HubGate = lazy(() => import('./pages/connectors/HubGate'));

// Documentation pages
const ApiDocs = lazy(() => import('./pages/documentation/ApiDocs'));

// Demo pages (work-in-progress UI previews — not yet wired to real flows)

// ---------------------------------------------------------------------------
// Route Guards
// ---------------------------------------------------------------------------

function ProtectedRoute({ children }) {
  const { isAuthenticated, isInitialized } = useAuthStore();

  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-theme-primary">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth/login" replace />;
  }

  return (
    <>
      <IdleTimeout />
      {children}
    </>
  );
}

function AdminRoute({ children }) {
  const { isAuthenticated, user } = useAuthStore();

  if (!isAuthenticated) return <Navigate to="/auth/login" replace />;
  if (!user?.is_superuser) return <Navigate to="/dashboard" replace />;
  return children;
}

function AdminOrEnterpriseRoute({ children }) {
  const { isAuthenticated, user } = useAuthStore();

  if (!isAuthenticated) return <Navigate to="/auth/login" replace />;
  const isEnterpriseAdmin = user?.profile?.account_type === 'enterprise_admin';
  if (!user?.is_superuser && !isEnterpriseAdmin) return <Navigate to="/dashboard" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { isAuthenticated, isInitialized } = useAuthStore();

  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-theme-primary">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500" />
      </div>
    );
  }

  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return children;
}

function CatchAllRoute() {
  const { isAuthenticated } = useAuthStore();
  return <Navigate to={isAuthenticated ? '/dashboard' : '/'} replace />;
}

// ---------------------------------------------------------------------------
// Themed Toaster
// ---------------------------------------------------------------------------

function ThemedToaster() {
  const { theme } = useUIStore();
  const isMobile = useMobile();
  const isDark = theme === 'dark';

  return (
    <Toaster
      position={isMobile ? 'top-center' : 'top-right'}
      toastOptions={{
        duration: 4000,
        style: {
          background: isDark ? '#1e2746' : '#ffffff',
          color: isDark ? '#f1f5f9' : '#1a1a2e',
          border: `1px solid ${isDark ? '#2d3748' : '#e5e5e5'}`,
          borderRadius: '12px',
          boxShadow: isDark
            ? '0 10px 15px -3px rgba(0, 0, 0, 0.5)'
            : '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
          ...(isMobile && { marginTop: '60px' }),
        },
        success: {
          style: { background: isDark ? '#1e2746' : '#ffffff', border: '1px solid #00d4aa' },
          iconTheme: { primary: '#00d4aa', secondary: isDark ? '#1e2746' : '#ffffff' },
        },
        error: {
          style: { background: isDark ? '#1e2746' : '#ffffff', border: '1px solid #e74c3c' },
          iconTheme: { primary: '#e74c3c', secondary: isDark ? '#1e2746' : '#ffffff' },
        },
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Theme Initializer
// ---------------------------------------------------------------------------

function ThemeInitializer({ children }) {
  const { theme } = useUIStore();

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [theme]);

  return children;
}

// ---------------------------------------------------------------------------
// Responsive Layout wrapper
//
// CRITICAL FIX: Previously isMobile was evaluated at the Route element level
// as JSX (e.g. element={isMobile ? <MobileView> : <DesktopView>}).
// React Router evaluates element props once when rendering the route tree,
// NOT reactively. If isMobile changes (or starts incorrect on first paint in
// Capacitor), the wrong element is already committed and won't swap.
//
// Fix: Move the isMobile decision INSIDE the layout component so it re-renders
// reactively. The Layout wrapper itself reads isMobile on every render, and
// child page components are rendered inside via <Outlet/>.
// ---------------------------------------------------------------------------

/**
 * ResponsiveLayout — mounts either MobileLayout or AppLayout based on
 * current viewport. Also exposes platform info to children via data attribute
 * on the root element for CSS targeting.
 */
function ResponsiveLayout() {
  const isMobile = useMobile();
  const platform = usePlatform();

  // Expose platform to DOM so CSS can target it: [data-platform="android"]
  useEffect(() => {
    document.documentElement.setAttribute('data-platform', platform);
  }, [platform]);

  return isMobile ? <MobileLayout /> : <AppLayout />;
}

// ---------------------------------------------------------------------------
// Page resolver — picks the right page component inside the layout
//
// CRITICAL FIX: Previously routes were defined as:
//   <Route path="/documents/:id" element={isMobile ? <MobileDocumentView /> : <DocumentDetail />} />
//
// The element is evaluated at route-tree render time (inside <Routes>), where
// isMobile may not yet have stabilized (WebView first paint). This causes the
// wrong component to mount, which then never unmounts because React Router
// doesn't re-evaluate elements after initial render without a key change.
//
// Fix: All page-level mobile/desktop branching happens INSIDE the layout's
// <Outlet> target — i.e. inside wrapper components below — so they re-render
// whenever isMobile changes.
// ---------------------------------------------------------------------------

function ResponsiveDashboard() {
  const isMobile = useMobile();
  return isMobile ? <MobileHome /> : <Dashboard />;
}

function ResponsiveUpload() {
  return <Upload />;
}

function ResponsiveDocuments() {
  return <Documents />;
}

/**
 * ResponsiveDocumentView — the critical viewer component.
 *
 * CRITICAL FIX: The mobile document viewer was sometimes blank because:
 * 1. isMobile was false on first render (WebView 0px width bug)
 * 2. DocumentDetail mounted instead of MobileDocumentView
 * 3. DocumentDetail's desktop CSS (overflow-hidden, h-screen flex) broke
 *    inside MobileLayout's <main> which has pt-14 pb-20 min-h-screen
 *
 * This wrapper ensures the correct component renders after isMobile stabilizes.
 */
function ResponsiveDocumentView() {
  return <DocumentDetail />;
}

function ResponsiveSettings() {
  const isMobile = useMobile();
  return isMobile ? <MobileSettings /> : <Settings />;
}

/**
 * SettingsRedirect — redirects old /settings route to the new settings layout
 * based on user role.
 */
function SettingsRedirect() {
  const { user } = useAuthStore();
  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab');

  const isAdmin = user?.is_superuser;

  if (isAdmin) {
    // Map old tab params to new routes
    const tabMap = { users: 'users', roles: 'roles', teams: 'teams', system: 'system' };
    const target = tabMap[tab] || 'users';
    return <Navigate to={`/admin/settings/${target}`} replace />;
  }

  return <Navigate to="/user/settings/profile" replace />;
}

// ---------------------------------------------------------------------------
// App
// ---------------------------------------------------------------------------

function App() {
  const { initializeAuth } = useAuthStore();

  useEffect(() => {
    initializeAuth();

    // Handle Capacitor hardware back button
    const handleBackButton = () => {
      // Basic implementation: attempt to go back
      if (window.location.pathname !== '/' && window.location.pathname !== '/dashboard') {
        window.history.back();
      }
    };
    window.addEventListener('capacitor:backButton', handleBackButton);

    return () => {
      window.removeEventListener('capacitor:backButton', handleBackButton);
    };
  }, []);

  return (
    <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID}>
      <BrowserRouter>
        <ThemeInitializer>
          <ThemedToaster />

          <Suspense fallback={<LazyFallback />}>
          <Routes>
            {/* Landing — public */}
            <Route path="/" element={<PublicRoute><Landing /></PublicRoute>} />
            <Route path="/security" element={<SecurityPage />} />

            {/* Auth — public */}
            <Route path="/auth/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/login" element={<Navigate to="/auth/login" replace />} />
            <Route path="/auth/signup" element={<Signup />} />
            <Route path="/auth/signup/success" element={<SignupSuccess />} />
            <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
            <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />

            {/* Public docs */}
            <Route path="/api-docs" element={<PublicApiDocs />} />
            <Route path="/terms" element={<TermsOfService />} />

            {/* Demo / preview routes — public so team can review without login */}
            <Route path="/structured-data-demo" element={<StructuredDataDemo />} />

            {/* ----------------------------------------------------------------
                Protected routes — all share ResponsiveLayout as their parent.
                ResponsiveLayout renders either MobileLayout or AppLayout, both
                of which render <Outlet /> for child routes.

                Page components use Responsive* wrappers so mobile/desktop
                branching happens INSIDE each component render, not at route
                definition time.
            ---------------------------------------------------------------- */}
            <Route
              element={
                <ProtectedRoute>
                  <ResponsiveLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/dashboard" element={<ResponsiveDashboard />} />
              <Route path="/upload" element={<ResponsiveUpload />} />
              <Route path="/documents" element={<ResponsiveDocuments />} />

              {/* ← The critical viewer route */}
              <Route path="/documents/:id" element={<ResponsiveDocumentView />} />

              {/* These routes don't need mobile variants — they work in both layouts */}
              <Route path="/documents/:id/crop" element={<DocumentCrop />} />

              {/* IDP Pipeline */}
              <Route path="/idp" element={<IDPList />} />
              <Route path="/idp/:id" element={<IDPDetail />} />

              {/* Connector Hub (superuser only) */}
              <Route path="/connectors" element={<AdminRoute><ConnectionsPage /></AdminRoute>} />
              <Route path="/connectors/connections/new" element={<AdminRoute><HubGate><CreateConnectionWizard /></HubGate></AdminRoute>} />
              <Route path="/connectors/connections/:key" element={<AdminRoute><HubGate><ConnectionDetailPage /></HubGate></AdminRoute>} />
              <Route path="/connectors/automations" element={<AdminRoute><AutomationsPage /></AdminRoute>} />
              <Route path="/connectors/automations/canvas" element={<AdminRoute><HubGate><AutomationCanvasPage /></HubGate></AdminRoute>} />
              <Route path="/connectors/automations/canvas/:key" element={<AdminRoute><HubGate><AutomationCanvasPage /></HubGate></AdminRoute>} />
              <Route path="/connectors/flows" element={<AdminRoute><ConnectorsPage /></AdminRoute>} />
              <Route path="/connectors/templates" element={<AdminRoute><TemplatesPage /></AdminRoute>} />
              <Route path="/connectors/runs" element={<AdminRoute><RunsPage /></AdminRoute>} />
              <Route path="/connectors/runs/:id" element={<AdminRoute><HubGate><RunDetailPage /></HubGate></AdminRoute>} />


              {/* Recycle Bin */}
              <Route path="/recycle-bin" element={<RecycleBin />} />
              <Route path="/pages/:id/edit" element={<PageEditor />} />

              <Route path="/tools" element={<ToolCatalogPage />} />

              {/* Individual tool pages */}
              <Route path="/tools/word-to-pdf" element={<WordToPdfPage />} />
              <Route path="/tools/pdf-to-word" element={<PdfToWordPage />} />
              <Route path="/tools/unzip" element={<UnzipPage />} />
              <Route path="/tools/translate-pdf" element={<TranslatePdfPage />} />

              {/* Organize PDF */}
              <Route path="/tools/merge-pdf" element={<MergePdfPage />} />
              <Route path="/tools/split-pdf" element={<SplitPdfPage />} />
              <Route path="/tools/remove-pages" element={<RemovePagesPage />} />
              <Route path="/tools/extract-pages" element={<ExtractPagesPage />} />
              <Route path="/tools/organize-pdf" element={<OrganizePdfPage />} />
              <Route path="/tools/scan-to-pdf" element={<ScanToPdfPage />} />

              {/* Optimize PDF */}
              <Route path="/tools/compress-pdf" element={<CompressPdfPage />} />
              <Route path="/tools/repair-pdf" element={<RepairPdfPage />} />
              <Route path="/tools/ocr-pdf" element={<OcrPdfPage />} />

              {/* Convert to PDF */}
              <Route path="/tools/ppt-to-pdf" element={<PptToPdfPage />} />
              <Route path="/tools/excel-to-pdf" element={<ExcelToPdfPage />} />
              <Route path="/tools/html-to-pdf" element={<HtmlToPdfPage />} />
              <Route path="/tools/jpg-to-pdf" element={<JpgToPdfPage />} />

              {/* Convert from PDF */}
              <Route path="/tools/pdf-to-jpg" element={<PdfToJpgPage />} />

              <Route path="/tools/pdf-to-ppt" element={<PdfToPptPage />} />
              <Route path="/tools/pdf-to-excel" element={<PdfToExcelPage />} />
              <Route path="/tools/pdf-to-pdfa" element={<PdfToPdfAPage />} />

              {/* Edit PDF */}
              <Route path="/tools/rotate-pdf" element={<RotatePdfPage />} />
              <Route path="/tools/add-page-numbers" element={<AddPageNumbersPage />} />
              <Route path="/tools/add-watermark" element={<AddWatermarkPage />} />
              <Route path="/tools/edit-pdf" element={<EditPdfPage />} />

              {/* Security */}
              <Route path="/tools/protect-pdf" element={<ProtectPdfPage />} />
              <Route path="/tools/unlock-pdf" element={<UnlockPdfPage />} />
              <Route path="/tools/redact-pdf" element={<RedactPdfPage />} />
              <Route path="/tools/flatten-pdf" element={<FlattenPdfPage />} />

              {/* Advanced AI / OCR */}
              <Route path="/tools/extract-tables" element={<ExtractTablesPage />} />
              <Route path="/tools/extract-images" element={<ExtractImagesPage />} />
              <Route path="/tools/pdf-to-text" element={<PdfToTextPage />} />
              <Route path="/tools/compare-pdf" element={<ComparePdfPage />} />

              {/* Old /settings routes — redirect to new settings layout */}
              <Route path="/settings" element={<SettingsRedirect />} />
              <Route path="/settings/organisation" element={<OrganisationSettings />} />

              <Route path="/document-types" element={<DocumentTypes />} />
              <Route path="/api-keys" element={<UserAPIKeys />} />
              <Route path="/api-tokens" element={<APITokens />} />
              <Route path="/fine-tune" element={<FineTune />} />
              <Route path="/enterprise/upgrade" element={<EnterpriseUpgrade />} />

              <Route path="/org/users" element={<OrgUsersList />} />
              <Route path="/org/users/add" element={<OrgUserForm />} />
              <Route path="/org/users/:id/edit" element={<OrgUserForm />} />
              <Route path="/org/custom-fields" element={<CustomFields />} />

              <Route path="/users" element={<Navigate to="/admin/settings/users" replace />} />
              <Route path="/statistics" element={<Navigate to="/analytics/my-activity" replace />} />
              <Route path="/analytics/my-activity" element={<MyActivity />} />
              <Route path="/analytics/org-overview" element={<OrgOverview />} />
              <Route path="/analytics/platform-overview" element={<PlatformOverview />} />
              <Route path="/admin-api-docs" element={<AdminRoute><AdminAPIDocs /></AdminRoute>} />
              <Route path="/admin/tenant-management" element={<AdminRoute><TenantManagement /></AdminRoute>} />
              <Route path="/signup-requests" element={<Navigate to="/admin/tenant-management?tab=signups" replace />} />
              <Route path="/admin/enterprise-requests" element={<Navigate to="/admin/tenant-management?tab=enterprise" replace />} />
              <Route path="/email-templates" element={<AdminRoute><EmailTemplates /></AdminRoute>} />
              <Route path="/admin/tool-settings" element={<AdminRoute><ToolSettingsPage /></AdminRoute>} />
              <Route path="/admin/roles" element={<Navigate to="/admin/settings/roles" replace />} />
              <Route path="/admin/roles/new" element={<AdminRoute><RoleWizard /></AdminRoute>} />
              <Route path="/admin/roles/:roleId" element={<AdminRoute><RoleWizard /></AdminRoute>} />


              <Route path="/feedback-support" element={<FeedbackSupport />} />
            </Route>

            {/* ── Admin Settings Layout (superuser only) ── */}
            <Route
              element={
                <AdminRoute>
                  <SettingsLayout />
                </AdminRoute>
              }
            >
              <Route path="/admin/settings" element={<Navigate to="/admin/settings/users" replace />} />
              <Route path="/admin/settings/users" element={<Users />} />
              <Route path="/admin/settings/roles" element={<RolesManagement />} />
              <Route path="/admin/settings/roles/new" element={<RoleWizard />} />
              <Route path="/admin/settings/roles/:roleId" element={<RoleWizard />} />
              <Route path="/admin/settings/teams" element={<Settings forceTab="teams" />} />
              <Route path="/admin/settings/system" element={<Settings forceTab="system" />} />
            </Route>

            {/* ── Enterprise Admin Settings (superuser + enterprise admin) ── */}
            <Route
              element={
                <AdminOrEnterpriseRoute>
                  <SettingsLayout />
                </AdminOrEnterpriseRoute>
              }
            >
              <Route path="/admin/settings/org-users" element={<OrgUsersList />} />
              <Route path="/admin/settings/org-users/add" element={<OrgUserForm />} />
              <Route path="/admin/settings/org-users/:id/edit" element={<OrgUserForm />} />
              <Route path="/admin/settings/custom-fields" element={<CustomFields />} />
              <Route path="/admin/settings/org-settings" element={<OrganisationSettings />} />
            </Route>

            {/* ── User Settings Layout ── */}
            <Route
              element={
                <ProtectedRoute>
                  <SettingsLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/user/settings" element={<Navigate to="/user/settings/profile" replace />} />
              <Route path="/user/settings/profile" element={<OrganisationSettings />} />
              <Route path="/user/settings/system" element={<Settings forceTab="system" />} />
            </Route>

            <Route path="*" element={<CatchAllRoute />} />
          </Routes>
          </Suspense>
        </ThemeInitializer>
      </BrowserRouter>
    </GoogleOAuthProvider>
  );
}

function AppWithErrorBoundary() {
  return (
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
}

export default AppWithErrorBoundary;